import React, { useEffect, useRef } from 'react';

const LAYERS = [
  "M859.7,785.1c0,159.6-119.3,258.8-311.3,258.8-177,0-293.2-87.7-327-246.9-2.1-9.6.3-19.3,6.4-26.9,6.4-7.9,15.9-12.2,25.9-12,15.1,0,28.4,10.7,31.5,25.4,27.3,128.1,121.3,198.6,264.4,198.6,133.2.5,243.4-59,244.3-194.4,10.2-239.6-324.4-196.2-458.4-313.6-49.6-40.2-72.8-92.3-72.8-163.9,0-143,108.5-231.9,283.1-231.9,89.9,0,161.8,23.5,213.7,70,41.1,36.7,69,87.9,80.6,147.9,1.8,9.6-.7,19.3-6.8,26.8-16.8,20.6-50.6,12.7-56.2-13.3-25.4-112.5-103.6-169.5-232.5-169.5-137.4,0-216.2,61-216.2,167.5-4.7,275,540.1,111.9,531.3,477.5Z",
  "M845.6,457.4c-11.9,10.8-30.3,10-41.1-1.9-30-33-81-44.2-130.2-55.1-58-12.8-123-30.6-161.5-84.3-9.4-13.1-6.4-31.2,6.6-40.6,13.1-9.3,31.3-6.4,40.7,6.7,26.1,36.5,78.2,50.6,124.9,61l1.8.4c55.4,12.3,118.2,26.2,160.6,72.8,10.9,11.8,10,30.3-1.8,41.1Z",
  "M897.7,685c-15.3,3.5-31.3-6.2-34.8-21.9-21.1-96-134.8-145.6-224.9-169.5-77.2-22.8-179.2-45-235.5-103.7-53.6-54.7-51.7-141.5,11-186.3,101.7-69.3,241.2-39.4,302.6,61.6,8.4,13.7,4.2,31.5-9.5,40-13.7,8.5-31.5,4.2-40-9.5-20.3-32.8-53.4-57.2-90.7-66.9-49.3-12.7-150.2,1.2-153.8,68.9.9,88.5,161.5,117.7,232.1,140.1,112.4,29.4,238.5,92.4,265.5,212.5,3.6,15.7-6.3,31.3-21.9,34.8Z",
  "M736.6,825.8c-5.2,15.3-22.2,23.2-36.9,18-15.1-5.2-23.2-21.8-18-37,9.6-27.8-5.4-59.7-24.5-79.1-45.3-43.3-113.8-56-171.5-74.4-140.5-35.5-294.1-103.9-324.2-248.2-3.1-15.8,7-31.1,22.8-34.3,15.8-3.1,31.1,7,34.2,22.7,12.4,60.9,63,116.9,139.3,153.8,69.8,33.7,150.7,49.5,225.8,75.1,38.2,13.1,81.8,30.9,114.9,64.4,38.7,39.1,53.6,93.6,38,138.9Z",
  "M635.6,846.2c-7.3,48-57,86.1-108.5,85.1-70.8.7-127.1-56.5-167.3-110-22.8-31.1,20.3-65.5,45.5-36.2,32.3,39.4,70.2,92,130.2,87.5,21.2-2.7,39.9-18.4,42.8-35.7,3.8-23.9-19.2-48.1-42.8-58.8-21.9-9.8-47.1-13.4-73.8-17.2-104.4-11.7-203.7-61.4-273.8-140.1-24.5-29.6,16.2-66.6,43.3-38.8,54.3,60.6,128.3,102,208.4,116.7,40.2,7.2,80.5,8.8,119.8,26.5,43.1,19.3,85.4,65.6,76.4,121.1Z",
  "M643.5,1144c-33,8.8-66.9,13.2-100.8,13.2s-71.3-4.9-106-14.5c-36.6-12-21.9-65,15.8-56.1,57.5,16.2,118.3,16.6,176,1.2,37.7-8.3,51.9,44.7,15,56.2Z",
];

const STAG     = 130;
const FADE_IN  = 420;
const HOLD     = 350;
const FADE_OUT = 600;

export default function SplashScreen({ onComplete }) {
  const bgRef    = useRef(null);
  const pathRefs = useRef([]);

  useEffect(() => {
    const bg    = bgRef.current;
    const paths = pathRefs.current.filter(Boolean);
    if (!bg || paths.length === 0) return;

    let cancelled = false;

    paths.forEach((el, i) => {
      el.style.transition = `opacity ${FADE_IN}ms cubic-bezier(0.4,0,0.2,1)`;
      setTimeout(() => {
        if (cancelled) return;
        el.style.opacity = '1';
      }, i * STAG);
    });

    const allInAt  = STAG * (paths.length - 1) + FADE_IN + HOLD;
    const exitTimer = setTimeout(() => {
      if (cancelled) return;
      bg.style.transition = `opacity ${FADE_OUT}ms cubic-bezier(0.4,0,0.2,1)`;
      bg.style.opacity = '0';
    }, allInAt);

    const doneTimer = setTimeout(() => {
      if (cancelled) return;
      if (onComplete) onComplete();
    }, allInAt + FADE_OUT);

    return () => {
      cancelled = true;
      clearTimeout(exitTimer);
      clearTimeout(doneTimer);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      ref={bgRef}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: '#0d1e28',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        opacity: 1,
      }}
    >
      {/* Fingerprint-S icon */}
      <svg
        viewBox="0 0 1080 1302.24"
        style={{ width: 72, height: 72, marginBottom: 24 }}
      >
        {LAYERS.map((d, i) => (
          <path
            key={i}
            ref={el => { pathRefs.current[i] = el; }}
            d={d}
            fill="#AFE1F6"
            style={{ opacity: 0 }}
          />
        ))}
      </svg>

      {/* Wordmark */}
      <svg viewBox="0 0 1080 164.33" style={{ height: 18, width: 'auto', display: 'block' }}>
        <defs><style>{'.wla{fill:#e0f5fc}'}</style></defs>
        <path className="wla" d="M63.72,133.19c15.09,0,24.19-7.43,24.19-17.25,0-34.25-83.12-9.58-83.12-70.19C4.79,19.64,28.99,0,61.32,0s57.73,22.28,57.73,48.87h-35.93c0-8.86-9.34-17.73-21.8-17.73s-21.8,6.23-21.8,14.61c0,29.94,84.32,11.5,84.32,66.35,0,29.94-26.83,52.22-62.52,52.22S0,138.94,0,105.16h34.73c0,16.05,9.1,28.03,28.99,28.03Z"/>
        <path className="wla" d="M270.32,159.54v-15.33c-6.95,8.62-27.31,19.88-49.11,19.88-40.24,0-76.89-36.65-76.89-81.69S180.97,1.2,226.01,1.2s79.05,36.41,79.05,81.21v77.13h-34.73ZM226.01,132.95c24.91,0,45.75-21.8,45.75-50.54s-20.84-50.06-45.75-50.06-45.75,21.56-45.75,50.06,20.84,50.54,45.75,50.54Z"/>
        <path className="wla" d="M411.01,31.38c-21.32,0-35.69,14.85-35.69,37.13v91.03h-34.73v-91.03C340.58,27.79,368.85.48,411.01.24c42.16.24,70.43,27.55,70.43,68.27v91.03h-34.73v-91.03c0-22.28-14.37-37.13-35.69-37.13Z"/>
        <path className="wla" d="M510.01,82.4c0-44.79,36.65-81.21,81.68-81.21s81.69,36.41,81.69,81.21-36.65,81.69-81.69,81.69-81.68-36.65-81.68-81.69ZM591.69,132.95c24.91,0,45.75-21.8,45.75-50.54s-20.84-50.06-45.75-50.06-45.75,21.56-45.75,50.06,20.84,50.54,45.75,50.54Z"/>
        <path className="wla" d="M738.87,3.83v155.7h-34.74V3.83h34.74Z"/>
        <path className="wla" d="M846.73.24v31.14c-21.32,0-35.69,14.85-35.69,37.13v91.03h-34.73v-91.03c0-40.72,28.27-68.03,70.42-68.27Z"/>
        <path className="wla" d="M940.3,164.09c-45.03,0-81.68-36.65-81.68-81.69S895.26,1.2,940.3,1.2c41.44,0,86.72,34.73,80.49,92.7h-125.04c4.55,22.76,23,39.05,44.55,39.05,17.49,0,33.06-10.78,40.48-27.55h37.37c-9.82,33.77-40.96,58.69-77.85,58.69ZM982.7,64.92c-6.23-19.64-23-32.58-42.4-32.58s-36.65,13.17-43.12,32.82l85.52-.24Z"/>
        <path className="wla" d="M1058.5,9.54c5.33,0,9.31,3.43,9.31,7.99,0,4.06-3.15,7.22-7.62,7.85l9.09,10.78h-6.12l-8.98-10.65h-2.97v10.65h-5.19V9.54h12.49ZM1058.5,21.18c2.26,0,3.94-1.56,3.94-3.66s-1.68-3.66-3.94-3.66h-7.3v7.32h7.3Z"/>
        <path className="wla" d="M1056.54,47.07c-12.94,0-23.46-10.52-23.46-23.46S1043.6.15,1056.54.15s23.46,10.52,23.46,23.46-10.52,23.46-23.46,23.46ZM1056.54,3.13c-11.29,0-20.48,9.19-20.48,20.48s9.19,20.48,20.48,20.48,20.48-9.19,20.48-20.48-9.19-20.48-20.48-20.48Z"/>
      </svg>

      <div style={{
        marginTop: 12,
        fontSize: '.65rem', letterSpacing: '.14em',
        color: 'rgba(175,225,246,0.3)',
        textTransform: 'uppercase',
        fontFamily: 'system-ui,-apple-system,"Helvetica Neue",Arial,sans-serif',
      }}>
        Admin Console
      </div>
    </div>
  );
}
